import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  clone,
  makeId,
  nowIso,
  normalizeMedicationCarePlanData,
  normalizeTestCarePlanData,
  normalizeMonitoringCarePlanData,
  generateTasksFromRecurrence,
  mockAvailableDoctors,
  mockPatients,
} from "../mock-apis-ipd/patientApi";
import { istDayKey, toIstWallClock } from "../../../utils/istDateTime";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/** Same host as bed allocation: list admissions from local/dev API (not manage-dev POST target). */
const IPD_ADMISSION_LIST_BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL || "http://localhost.dev.com:8000/app/api/";

const defaultVitals = () => ({
  bloodPressure: "--/--",
  heartRate: "--",
  temperature: "--",
  oxygenSaturation: "--",
  respiratoryRate: "--",
  weight: "--",
  lastUpdated: new Date().toLocaleString(),
});

// ipd_admission (?as=admitting) now nests diagnosis as [{ provisional_diagnosis, final_diagnosis }]
// instead of a plain string — extract display text without leaking the raw object/array into the UI.
const getDiagnosisText = (diagnosis) => {
  if (typeof diagnosis === "string") return diagnosis;
  const entry = Array.isArray(diagnosis) ? diagnosis[0] : diagnosis;
  if (!entry || typeof entry !== "object") return "";
  return entry.final_diagnosis || entry.provisional_diagnosis || "";
};

// [PHARMACY_RX] add_ipd_prescription/ needs provisional_diagnosis and
// final_diagnosis as separate fields (not the collapsed `condition` display
// string above) — pull them individually from the same nested entry.
const getDiagnosisParts = (diagnosis) => {
  const entry = Array.isArray(diagnosis) ? diagnosis[0] : diagnosis;
  if (!entry || typeof entry !== "object") return { provisional: "", final: "" };
  return {
    provisional: entry.provisional_diagnosis || "",
    final: entry.final_diagnosis || "",
  };
};

// [PHARMACY_RX] Registration captures allergies as one free-text field
// (RegisterPatientForIpd "allergies" input) — split into a plain string list
// for add_ipd_prescription/'s allergies[] array.
const getAllergiesList = (allergies) => {
  if (Array.isArray(allergies)) return allergies.filter(Boolean);
  if (typeof allergies === "string") {
    return allergies.split(/[,;]/).map((a) => a.trim()).filter(Boolean);
  }
  return [];
};

const normalizeAdmittedPatients = (payload, existingPatients = []) => {
  const list =
    payload?.admission_list ||
    payload?.ipd_admissions ||
    payload?.results ||
    payload?.data ||
    (Array.isArray(payload) ? payload : []);

  if (!Array.isArray(list)) return [];

  return list.map((item, index) => {
    const stableId = String(item?.admission_id || item?.ipd_admission_id || item?.id || index + 1);
    const existing = existingPatients.find((patient) => String(patient.id) === stableId);
    const resolvedPatientIdentifier =
      item?.pat_id ||
      item?.patient_id ||
      item?.patient?.pat_id ||
      item?.patient?.patient_id ||
      item?.patient_details?.pat_id ||
      item?.patient_details?.patient_id ||
      existing?.pat_id ||
      existing?.patient_id ||
      existing?.patientId ||
      "";
    const dob =
      item?.date_of_birth ||
      item?.dob ||
      item?.patient?.date_of_birth ||
      item?.patient?.dob ||
      item?.patient_details?.date_of_birth ||
      item?.patient_details?.dob ||
      existing?.dob ||
      null;
    const computedAgeFromDob = (() => {
      if (!dob || typeof dob !== "string") return null;
      const year = Number(dob.split("-")[0]);
      if (!Number.isFinite(year)) return null;
      return new Date().getFullYear() - year;
    })();

    return {
      id: stableId,
      pat_id: resolvedPatientIdentifier,
      patient_id: resolvedPatientIdentifier,
      patientId: resolvedPatientIdentifier,
      uhid:
        item?.patient?.uhid ||
        item?.uhid ||
        item?.patient_details?.uhid ||
        existing?.uhid ||
        "",
      admissionId:
        item?.admission_id ||
        item?.ipd_admission_id ||
        item?.id ||
        existing?.admissionId ||
        0,
      name:
        item?.patient_name ||
        item?.name ||
        item?.patient?.name ||
        item?.patient_details?.name ||
        `Patient ${index + 1}`,
      age:
        item?.age ||
        item?.patient_age ||
        item?.patient?.age ||
        item?.patient_details?.age ||
        computedAgeFromDob ||
        existing?.age ||
        null,
      gender:
        item?.gender ||
        item?.sex ||
        item?.patient_gender ||
        item?.patient?.gender ||
        item?.patient?.sex ||
        item?.patient_details?.gender ||
        item?.patient_details?.sex ||
        existing?.gender ||
        null,
      bloodGroup:
        item?.patient?.blood_group ||
        item?.blood_group ||
        item?.patient_details?.blood_group ||
        existing?.bloodGroup ||
        "",
      dob,
      wardNo:
        item?.ward_no ||
        item?.ward ||
        item?.patient?.ward_no ||
        item?.patient_details?.ward_no ||
        "-",
      roomNo:
        item?.room_no ||
        item?.room ||
        item?.room_id ||
        item?.patient?.room_no ||
        item?.patient_details?.room_no ||
        "-",
      bedNo:
        item?.bed_no ||
        item?.bed_id ||
        item?.bed_number ||
        item?.allocated_bed_id ||
        item?.patient?.bed_no ||
        item?.patient_details?.bed_no ||
        "-",
      bed_type_name: item?.bed_type_name || "-",
      location: item?.location || "-",
      type: existing?.type || "Main",
      condition:
        item?.condition ||
        getDiagnosisText(item?.diagnosis) ||
        item?.reason ||
        item?.admission_reason ||
        item?.chief_complaint ||
        existing?.condition ||
        "No condition details available",
      // [PHARMACY_RX] Separate provisional/final diagnosis for add_ipd_prescription/.
      provisionalDiagnosis:
        getDiagnosisParts(item?.diagnosis).provisional ||
        existing?.provisionalDiagnosis ||
        "",
      finalDiagnosis:
        getDiagnosisParts(item?.diagnosis).final ||
        existing?.finalDiagnosis ||
        "",
      allergies:
        getAllergiesList(item?.allergies).length
          ? getAllergiesList(item?.allergies)
          : existing?.allergies || [],
      diseases: existing?.diseases || [],
      admissionDate:
        // admitted_at / created_at are UTC instants — their date half belongs to
        // the previous day for anything admitted after 18:30 IST.
        istDayKey(item?.admitted_at) ||
        item?.admission_date ||
        item?.admitted_on ||
        istDayKey(item?.created_at) ||
        existing?.admissionDate ||
        null,
      carePlans: existing?.carePlans || [],
      tasks: existing?.tasks || [],
      vitals: existing?.vitals || defaultVitals(),
      // [LAB_TEST] From ipd_admission admission_details — used by CarePlanActivityModal on Test save.
      careContextRef:
        item?.admission_details?.care_context_ref ||
        item?.care_context_ref ||
        existing?.careContextRef ||
        "",
      doctorName:
        item?.doctor_name ||
        item?.doctors?.admitting?.name ||
        existing?.doctorName ||
        "",
      // Set by nurse via discharge_initiation API; drives doctor-console discharge workflow.
      dischargeInitiated: Boolean(
        item?.admission_details?.discharge_initiated ?? existing?.dischargeInitiated
      ),
    };
  });
};

const normalizeNurseTaskList = (payload = [], patientMap = {}) => {
  const list = Array.isArray(payload) ? payload : [];
  return list.map((item, index) => {
    const admissionKey = String(item?.admission_id || "");
    const patient = patientMap[admissionKey] || {};
    const compactDetail = String(item?.compact_detail || "").trim();
    const compactParts = compactDetail
      ? compactDetail
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean)
      : [];

    return {
      id: item?.id || item?.task_id || `${item?.care_plan_id || "task"}-${index}`,
      templateId: item?.id || item?.task_id || `${item?.care_plan_id || "task"}-${index}`,
      patientId: patient?.id || admissionKey || "",
      patientName: patient?.name || "Patient",
      roomNo: patient?.roomNo || "-",
      wardNo: patient?.wardNo || "-",
      category:
        item?.task_type === "Medication"
          ? "Medicine"
          : item?.task_type === "Test"
            ? "Labs"
            : item?.task_type || "General",
      status:
        String(item?.status || "Pending").toLowerCase() === "completed"
          ? "Completed"
          : "Pending",
      priority:
        String(item?.priority || "").toLowerCase() === "high" ||
          String(item?.priority || "").toLowerCase() === "urgent"
          ? "High"
          : "Medium",
      dueDate:
        item?.due_date ||
        item?.scheduled_date ||
        item?.scheduled_datetime?.split?.("T")?.[0] ||
        "",
      dueTime:
        item?.due_time ||
        item?.scheduled_time ||
        item?.scheduled_datetime?.split?.("T")?.[1]?.slice(0, 5) ||
        "",
      scheduledDateTime:
        item?.scheduled_datetime ||
        item?.scheduledDateTime ||
        item?.occurrenceDateTime ||
        "",
      taskTitle: item?.task_type || compactParts[0] || "Nurse Task",
      description: compactParts.slice(1).join("\n") || item?.description || "",
      compact_detail: item?.compact_detail || "",
      admissionId: item?.admission_id || "",
      dischargeInitiated: Boolean(patient?.dischargeInitiated),
    };
  });
};

const extractCarePlanTasks = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.care_plan_tasks)) return payload.data.care_plan_tasks;
  if (Array.isArray(payload?.care_plan_tasks)) return payload.care_plan_tasks;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  return [];
};

const extractCarePlanList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.care_plans)) return payload.data.care_plans;
  if (Array.isArray(payload?.care_plans)) return payload.care_plans;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getPrimaryCarePlanData = (data) => {
  if (Array.isArray(data) && data.length > 0) {
    const first = data[0];
    return first && typeof first === "object" ? first : {};
  }
  if (data && typeof data === "object") return data;
  return {};
};

const normalizeNurseTasksFromCarePlans = (payload = [], patientMap = {}, structureNameById = {}) => {
  const list = extractCarePlanList(payload);
  return list.map((item, index) => {
    const admissionKey = String(item?.admission_id || "");
    const patient = patientMap[admissionKey] || {};
    const source = getPrimaryCarePlanData(item?.data);
    const scheduledDateTime =
      source?.scheduled_datetime ||
      source?.scheduledDateTime ||
      source?.start_datetime ||
      source?.startDateTime ||
      source?.occurrenceDateTime ||
      source?.start ||
      // Last resort when the plan carries no schedule of its own. created_at is
      // a UTC instant, unlike every other candidate above, so it is converted to
      // ward time here — the screen slices this string as a wall clock.
      toIstWallClock(item?.created_at) ||
      "";

    return {
      id: item?.id || `cp-${item?.care_plan_id || index}`,
      templateId: item?.id || `cp-${item?.care_plan_id || index}`,
      patientId: patient?.id || admissionKey || "",
      patientName: patient?.name || "Patient",
      roomNo: patient?.roomNo || "-",
      wardNo: patient?.wardNo || "-",
      category: "Care Plan",
      status: "Pending",
      priority:
        String(source?.priority || "").toLowerCase() === "high" ||
          String(source?.priority || "").toLowerCase() === "urgent"
          ? "High"
          : "Medium",
      dueDate: scheduledDateTime?.split?.("T")?.[0] || "",
      dueTime: scheduledDateTime?.split?.("T")?.[1]?.slice(0, 5) || "",
      scheduledDateTime,
      taskTitle:
        structureNameById[item?.care_plan_id] ||
        item?.structure_name ||
        item?.name ||
        "Nurse Task",
      description:
        source?.description ||
        source?.notes ||
        source?.reason ||
        source?.instruction ||
        "",
      admissionId: item?.admission_id || "",
    };
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const mapAdmissionToPatient = (admission) => {
  const attendingList = admission.doctors?.attending || [];
  const allDoctors = attendingList
    .filter((doc) => doc.role === "referral_in_ipd")
    .map((doc) => ({
      id: doc.id,
      name: doc.name,
      specialty: doc.specialty || "",
      initials: getInitials(doc.name),
      isPrimary: false,
      role: doc.role,
    }));
  const dob = admission.patient?.dob;
  let age = 0;
  if (dob) {
    const today = new Date();
    const birth = new Date(dob);
    age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  }
  return {
    id: String(admission.admission_id),
    admission_id: admission.admission_id,
    name: admission.patient?.name || "",
    wardNo: "",
    roomNo: "",
    bedNo: admission.bed_id || "",
    type: "Main",
    condition: "",
    diseases: [],
    age,
    gender: admission.patient?.gender === "F" ? "Female" : admission.patient?.gender === "M" ? "Male" : "",
    admissionDate: istDayKey(admission.admitted_at),
    vitals: { bloodPressure: "", heartRate: 0, temperature: 0, oxygenSaturation: 0, respiratoryRate: 0, weight: 0, lastUpdated: "" },
    carePlans: [],
    referredDoctors: allDoctors,
    tasks: [],
    patientId: admission.patient?.patient_id || "",
    careContextRef: admission.admission_details?.care_context_ref || "",
    doctorName: admission.doctor_name || admission.doctors?.admitting?.name || "",
  };
};

const IpdDataContext = createContext(null);

export function useIpdData() {
  const ctx = useContext(IpdDataContext);
  if (!ctx) {
    throw new Error("useIpdData must be used within IpdDataProvider");
  }
  return ctx;
}

export function IpdDataProvider({ children }) {
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [completedTaskInstances, setCompletedTaskInstances] = useState({});

  // useEffect(() => {
  //   const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
  //   const authToken = sessionStorage.getItem("accessToken") || "";
  //   axios
  //     .get(`${BASE}ipd_admission/`, {
  //       headers: { Authorization: `Bearer ${authToken}` },
  //     })
  //     .then((res) => {
  //       if (res.data?.data && Array.isArray(res.data.data)) {
  //         setPatients(res.data.data.map(mapAdmissionToPatient));
  //       } else {
  //         setPatients(clone(mockPatients));
  //       }
  //     })
  //     .catch(() => setPatients(clone(mockPatients)))
  //     .finally(() => setPatientsLoading(false));
  // }, []);

  const getPatients = useCallback(async () => {
    await delay(150);
    const authToken = sessionStorage.getItem("accessToken") || "";
    const headers = {
      Authorization: `Bearer ${authToken}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
      USERNAME: sessionStorage.getItem("userName") || "",
    };

    try {
      const response = await axios.get(
        `${IPD_ADMISSION_LIST_BASE}ipd_admission/?doctor_id=${sessionStorage.getItem("doctor_id")}&as=admitting&status=Allocated`,
        { headers }
      );
      const normalizedPatients = normalizeAdmittedPatients(response?.data, patients);

      setPatients(normalizedPatients);
      return { data: clone(normalizedPatients), status: 200 };
    } catch (error) {
      console.error("Failed to fetch admitted IPD patients:", error);
      return { data: clone(patients), status: 200 };
    }
  }, [patients]);

  const getPatientById = useCallback(
    async (id) => {
      await delay(200);
      const patient = patients.find((p) => p.id === id);
      if (!patient) {
        return { data: null, status: 404, error: "Patient not found" };
      }
      return { data: clone(patient), status: 200 };
    },
    [patients]
  );

  const searchPatients = useCallback(
    async (query) => {
      await delay(200);
      const lowercaseQuery = query.toLowerCase();
      const results = patients.filter(
        (p) =>
          String(p.name || "").toLowerCase().includes(lowercaseQuery) ||
          // Search what the "Ward" column actually renders (ward_name, falling
          // back to bed_type_name) — `wardNo` reads keys this endpoint never
          // returns, so on its own it made the visible ward unsearchable.
          String(p.bed_type_name || "").toLowerCase().includes(lowercaseQuery) ||
          String(p.bed_type_name || "").toLowerCase().includes(lowercaseQuery) ||
          String(p.wardNo || "").toLowerCase().includes(lowercaseQuery) ||
          String(p.roomNo || "").toLowerCase().includes(lowercaseQuery) ||
          String(p.bedNo || "").toLowerCase().includes(lowercaseQuery) ||
          String(p.condition || "").toLowerCase().includes(lowercaseQuery)
      );
      return { data: clone(results), status: 200 };
    },
    [patients]
  );

  const addCarePlan = useCallback(
    async (patientId, carePlan) => {
      await delay(300);
      const patientIndex = patients.findIndex((p) => p.id === patientId);
      if (patientIndex === -1) {
        return { data: null, status: 404, error: "Patient not found" };
      }

      let normalizedCarePlan = carePlan;
      if (carePlan?.type === "medication") {
        normalizedCarePlan = {
          ...carePlan,
          data: normalizeMedicationCarePlanData(carePlan.data),
        };
      } else if (carePlan?.type === "test") {
        normalizedCarePlan = {
          ...carePlan,
          data: normalizeTestCarePlanData(carePlan.data),
        };
      } else if (carePlan?.type === "monitoring") {
        normalizedCarePlan = {
          ...carePlan,
          data: normalizeMonitoringCarePlanData(carePlan.data),
        };
      }

      const newCarePlan = {
        ...normalizedCarePlan,
        id: makeId(),
        createdAt: nowIso(),
      };

      setPatients((prev) => {
        const next = clone(prev);
        if (!next[patientIndex].carePlans) next[patientIndex].carePlans = [];
        const plans = clone(next[patientIndex].carePlans || []);
        plans.push(newCarePlan);
        next[patientIndex].carePlans = plans;
        return next;
      });

      return { data: clone(newCarePlan), status: 201 };
    },
    [patients]
  );

  const stopMedication = useCallback(
    async (patientId, carePlanId, medicationId) => {
      await delay(200);
      const patientIndex = patients.findIndex((p) => p.id === patientId);
      if (patientIndex === -1) {
        return { data: null, status: 404, error: "Patient not found" };
      }

      const carePlans = patients[patientIndex].carePlans || [];
      const planIndex = carePlans.findIndex((cp) => cp.id === carePlanId);
      if (planIndex === -1) {
        return { data: null, status: 404, error: "Care plan not found" };
      }

      const plan = carePlans[planIndex];
      if (plan.type !== "medication") {
        return { data: null, status: 400, error: "Not a medication care plan" };
      }

      const normalizedPlan = { ...plan, data: normalizeMedicationCarePlanData(plan.data) };
      const medIndex = normalizedPlan.data.medications.findIndex((m) => m.id === medicationId);
      if (medIndex === -1) {
        return { data: null, status: 404, error: "Medication not found" };
      }

      const existing = normalizedPlan.data.medications[medIndex];
      if (existing.status === "stopped") {
        return { data: clone(plan), status: 200 };
      }

      const stoppedAt = nowIso();
      const updatedMed = {
        ...existing,
        status: "stopped",
        stoppedAt,
        endDate: existing.endDate || stoppedAt.slice(0, 10),
      };

      setPatients((prev) => {
        const next = clone(prev);
        next[patientIndex].carePlans = clone(next[patientIndex].carePlans || []);
        const planCopy = clone(next[patientIndex].carePlans[planIndex]);
        if (!planCopy.data) planCopy.data = { medications: [] };
        planCopy.data.medications = (planCopy.data.medications || []).map((m, i) =>
          i === medIndex ? updatedMed : m
        );
        next[patientIndex].carePlans[planIndex] = planCopy;
        return next;
      });

      const updatedPlan = { ...normalizedPlan, data: { ...normalizedPlan.data, medications: normalizedPlan.data.medications.map((m, i) => (i === medIndex ? updatedMed : m)) } };
      return { data: clone(updatedPlan), status: 200 };
    },
    [patients]
  );

  const getCarePlans = useCallback(
    async (patientId) => {
      await delay(200);
      const patient = patients.find((p) => p.id === patientId);
      if (!patient) {
        return { data: null, status: 404, error: "Patient not found" };
      }
      return { data: clone(patient.carePlans || []), status: 200 };
    },
    [patients]
  );

  const updateVitals = useCallback(
    async (patientId, vitals) => {
      await delay(200);
      const patientIndex = patients.findIndex((p) => p.id === patientId);
      if (patientIndex === -1) {
        return { data: null, status: 404, error: "Patient not found" };
      }

      const updated = {
        ...vitals,
        lastUpdated: new Date().toLocaleString(),
      };

      setPatients((prev) => {
        const next = clone(prev);
        next[patientIndex].vitals = updated;
        return next;
      });

      return { data: clone(updated), status: 200 };
    },
    [patients]
  );

  const getTasks = useCallback(
    async (patientId) => {
      await delay(200);
      const patient = patients.find((p) => p.id === patientId);
      if (!patient) {
        return { data: null, status: 404, error: "Patient not found" };
      }
      return { data: clone(patient.tasks || []), status: 200 };
    },
    [patients]
  );

  const saveTasks = useCallback(
    async (patientId, tasks) => {
      await delay(200);
      const patientIndex = patients.findIndex((p) => p.id === patientId);
      if (patientIndex === -1) {
        return { data: null, status: 404, error: "Patient not found" };
      }

      const taskList = clone(Array.isArray(tasks) ? tasks : []);

      setPatients((prev) => {
        const next = clone(prev);
        next[patientIndex].tasks = taskList;
        return next;
      });

      return { data: clone(taskList), status: 200 };
    },
    [patients]
  );

  const getNurseTasks = useCallback(async () => {
    await delay(200);
    const authToken = sessionStorage.getItem("accessToken") || "";
    const headers = {
      Authorization: `Bearer ${authToken}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
      USERNAME: sessionStorage.getItem("userName") || "",
    };

    try {
      const admissionsResponse = await axios.get(
        `${IPD_ADMISSION_LIST_BASE}ipd_admission/?status=Allocated`,
        { headers }
      );
      const admissionList =
        admissionsResponse?.data?.admission_list ||
        admissionsResponse?.data?.ipd_admissions ||
        admissionsResponse?.data?.results ||
        admissionsResponse?.data?.data ||
        [];
      const normalizedPatients = normalizeAdmittedPatients(admissionList, patients);
      const patientMap = normalizedPatients.reduce((acc, patient) => {
        acc[String(patient.admissionId || patient.id)] = patient;
        return acc;
      }, {});

      const allowedAdmissionIds = new Set(
        normalizedPatients.map((patient) => String(patient.admissionId || patient.id || ""))
      );

      // Prefer a single bulk fetch to avoid N network calls (one per admission).
      // If backend doesn't support it, fallback to per-admission requests.
      let mergedTasks = [];
      try {
        const bulkResponse = await axios.get(
          `${IPD_ADMISSION_LIST_BASE}care_plan_tasks/`,
          { headers }
        );
        const bulkList = extractCarePlanTasks(bulkResponse?.data);
        mergedTasks = bulkList.filter((task) =>
          allowedAdmissionIds.has(String(task?.admission_id || ""))
        );
      } catch (bulkError) {
        const taskRequests = normalizedPatients.map((patient) =>
          axios
            .get(
              `${IPD_ADMISSION_LIST_BASE}care_plan_tasks/?admission_id=${patient.admissionId || patient.id}`,
              { headers }
            )
            .then((res) => extractCarePlanTasks(res?.data))
            .catch(() => [])
        );
        const taskGroups = await Promise.all(taskRequests);
        mergedTasks = taskGroups.flat();
      }

      let normalizedTasks = normalizeNurseTaskList(mergedTasks, patientMap);

      // Fallback: if no generated care_plan_tasks are available, derive cards from care_plans.
      if (normalizedTasks.length === 0) {
        const structuresResponse = await axios.get(
          `${IPD_ADMISSION_LIST_BASE}care_plan_structures/`,
          { headers }
        ).catch(() => ({ data: {} }));
        const structureList = Array.isArray(structuresResponse?.data?.data)
          ? structuresResponse.data.data
          : [];
        const structureNameById = structureList.reduce((acc, item) => {
          const label = item?.name || "";
          if (item?.care_plan_id != null) acc[item.care_plan_id] = label;
          if (item?.id != null) acc[item.id] = label;
          if (item?.template_id != null) acc[item.template_id] = label;
          return acc;
        }, {});

        const carePlanRequests = normalizedPatients.map((patient) =>
          axios
            .get(
              `${IPD_ADMISSION_LIST_BASE}care_plans/?admission_id=${patient.admissionId || patient.id}`,
              { headers }
            )
            .then((res) =>
              normalizeNurseTasksFromCarePlans(res?.data, patientMap, structureNameById)
            )
            .catch(() => [])
        );
        const fallbackGroups = await Promise.all(carePlanRequests);
        normalizedTasks = fallbackGroups.flat();
      }
      return { data: clone(normalizedTasks), status: 200 };
    } catch (error) {
      console.error("Error loading nurse tasks from API:", error);
      return { data: [], status: 200 };
    }
  }, [patients]);

  const getPatientNurseTasks = useCallback(
    async (patientId) => {
      await delay(200);
      const patient = patients.find((p) => p.id === patientId);
      if (!patient) {
        return { data: null, status: 404, error: "Patient not found" };
      }
      const allTasks = [];
      if (patient.tasks && Array.isArray(patient.tasks)) {
        patient.tasks.forEach((taskTemplate) => {
          if (taskTemplate.recurrencePattern?.isRecurring) {
            const generatedTasks = generateTasksFromRecurrence(
              {
                ...taskTemplate,
                patientId: patient.id,
                patientName: patient.name,
                roomNo: patient.roomNo,
                wardNo: patient.wardNo,
              },
              taskTemplate.recurrencePattern,
              true,
              completedTaskInstances
            );
            allTasks.push(...generatedTasks);
          } else {
            allTasks.push({
              ...taskTemplate,
              id: taskTemplate.id,
              templateId: taskTemplate.id,
              patientId: patient.id,
              patientName: patient.name,
              roomNo: patient.roomNo,
              wardNo: patient.wardNo,
              dueDate:
                taskTemplate.dueDate ||
                taskTemplate.recurrencePattern?.startDateTime?.split("T")[0] ||
                "",
              dueTime:
                taskTemplate.dueTime ||
                taskTemplate.recurrencePattern?.startDateTime?.split("T")[1]?.slice(0, 5) ||
                "",
              scheduledDateTime:
                taskTemplate.recurrencePattern?.startDateTime ||
                (taskTemplate.dueDate && taskTemplate.dueTime
                  ? `${taskTemplate.dueDate}T${taskTemplate.dueTime}:00Z`
                  : taskTemplate.dueDate
                    ? `${taskTemplate.dueDate}T00:00:00Z`
                    : new Date().toISOString()),
            });
          }
        });
      }
      return { data: clone(allTasks), status: 200 };
    },
    [patients, completedTaskInstances]
  );

  const updateTaskStatus = useCallback(
    async (taskTemplateId, status, scheduledDateTime) => {
      await delay(200);
      if (taskTemplateId == null || scheduledDateTime == null) {
        return {
          data: { templateId: taskTemplateId, status, scheduledDateTime },
          status: 400,
        };
      }
      const instanceKey = `${taskTemplateId}-${scheduledDateTime}`;
      setCompletedTaskInstances((prev) => {
        const next = { ...prev };
        if (status === "Completed") {
          next[instanceKey] = true;
        } else {
          delete next[instanceKey];
        }
        return next;
      });
      return {
        data: { templateId: taskTemplateId, status, scheduledDateTime },
        status: 200,
      };
    },
    []
  );

  const isTaskInstanceCompleted = useCallback(
    (taskTemplateId, scheduledDateTime) => {
      return (
        completedTaskInstances[`${taskTemplateId}-${scheduledDateTime}`] || false
      );
    },
    [completedTaskInstances]
  );

  // assignedDoctorsOnly mirrors the backend's `assigned_doctors_only` flag,
  // which DEFAULTS TO TRUE for a facility user (views.py get_all_doctors_
  // based_on_department) — i.e. it returns only doctors who list the caller as
  // an assistant. That's right for a doctor picking a handover partner, but
  // wrong for ward/admin staff picking an attending doctor for someone else:
  // they're nobody's assistant, so they get an empty list. Pass false there.
  const getAvailableDoctors = useCallback(
    async (department, { assignedDoctorsOnly = true } = {}) => {
      const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
      const authToken = sessionStorage.getItem("accessToken") || "";
      try {
        const res = await axios.post(
          `${BASE}get_all_doctors_based_on_department/`,
          { department, assigned_doctors_only: assignedDoctorsOnly },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        return { data: res.data?.response_list || [], status: 200 };
      } catch (error) {
        console.error("Failed to fetch doctors:", error);
        return { data: [], status: 500 };
      }
    },
    []
  );

  // POST /api/patients – add a new patient from registration form
  const addPatient = useCallback(
    async (formData) => {
      await delay(200);

      const today = new Date();
      const dob = formData.dateOfBirth || formData.dob || "";
      let age = "";
      if (dob) {
        const birthYear = parseInt(dob.split("-")[0], 10);
        if (!isNaN(birthYear)) age = today.getFullYear() - birthYear;
      }

      const referredDoctors = [];
      if (formData.admitting_doctor_id) {
        referredDoctors.push({
          id: formData.admitting_doctor_id,
          name: formData.admitting_doctor_name || "Primary Doctor",
          specialty: formData.admitting_department || "",
          initials: getInitials(formData.admitting_doctor_name || "Primary Doctor"),
          isPrimary: true,
          role: "primary",
        });
      }

      if (Array.isArray(formData.attending_doctors)) {
        formData.attending_doctors.forEach((doc) => {
          referredDoctors.push({
            id: doc.doctor_id,
            name: doc.doctor_name || "Referred Doctor",
            specialty: doc.department_id || "",
            initials: getInitials(doc.doctor_name || "Referred Doctor"),
            isPrimary: false,
            role: "attending",
            notes: doc.notes || "",
          });
        });
      }

      const newPatient = {
        id: makeId(),
        name: formData.name || `${formData.firstname || ""} ${formData.middlename || ""} ${formData.lastname || ""}`.trim() || "Unknown",
        wardNo: formData.wardNo || "",
        roomNo: formData.roomNo || "",
        bedNo: formData.bedNo || "",
        type: "Main",
        condition: formData.referral_reason || formData.admissionType || "",
        diseases: [],
        age: age || 0,
        gender: formData.gender === "M" ? "Male" : formData.gender === "F" ? "Female" : formData.gender || "",
        admissionDate: nowIso().slice(0, 10),
        vitals: {
          bloodPressure: formData.bloodPressure || "",
          heartRate: formData.pulseRate ? Number(formData.pulseRate) : 0,
          temperature: formData.bodyTemperatureValue ? Number(formData.bodyTemperatureValue) : 0,
          oxygenSaturation: formData.oxygenSaturation ? Number(formData.oxygenSaturation) : 0,
          respiratoryRate: formData.respiratoryRate ? Number(formData.respiratoryRate) : 0,
          weight: formData.weight ? Number(formData.weight) : 0,
          lastUpdated: new Date().toLocaleString(),
        },
        carePlans: [],
        referredDoctors: referredDoctors,
        referral: formData.referral || null,
        tasks: [],
      };

      setPatients((prev) => [...prev, clone(newPatient)]);

      return { data: clone(newPatient), status: 201 };
    },
    []
  );

  const addReferredDoctor = useCallback(
    async (admissionId, doctor) => {
      const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
      const authToken = sessionStorage.getItem("accessToken") || "";

      try {
        await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${admissionId}/doctors/`,
          {
            doctor_id: doctor.id,
            // [OLD CODE]
            // role: "consultant",
            // [NEW CODE - Referred to Me feature]
            role: "referral_in_ipd",
            specialty: doctor.specialty,
            notes: doctor.notes ?? "",
          },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
              "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
              "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
              USERNAME: sessionStorage.getItem("userName") || "",
            },
          }
        );
      } catch (error) {
        console.error("Failed to add attending doctor:", error);
        return { status: 500, error: "Failed to add attending doctor" };
      }

      setPatients((prev) =>
        prev.map((patient) => {
          if (String(patient.id) !== String(admissionId)) return patient;
          const currentReferred = patient.referredDoctors || [];
          return {
            ...patient,
            referredDoctors: [...currentReferred, { ...doctor }],
          };
        })
      );

      return { status: 200, data: doctor };
    },
    []
  );

  const removeReferredDoctor = useCallback(
    async (admissionId, doctorId) => {
      const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
      const authToken = sessionStorage.getItem("accessToken") || "";

      try {
        await axios.delete(
          `${REACT_APP_BACKEND_ROUTE_URL}ipd_admission/${admissionId}/doctors/${doctorId}/`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
              "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
              "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
              USERNAME: sessionStorage.getItem("userName") || "",
            },
          }
        );
      } catch (error) {
        console.error("Failed to remove referred doctor:", error);
        return { status: 500, error: "Failed to remove referred doctor" };
      }

      setPatients((prev) =>
        prev.map((patient) => {
          if (String(patient.id) !== String(admissionId)) return patient;
          const currentReferred = patient.referredDoctors || [];
          return {
            ...patient,
            referredDoctors: currentReferred.filter((doc) => doc.id !== doctorId),
          };
        })
      );

      return { status: 200 };
    },
    []
  );

  const value = {
    patients,
    patientsLoading,
    completedTaskInstances,
    getPatients,
    getPatientById,
    searchPatients,
    addCarePlan,
    stopMedication,
    getCarePlans,
    updateVitals,
    getTasks,
    saveTasks,
    getNurseTasks,
    getPatientNurseTasks,
    updateTaskStatus,
    isTaskInstanceCompleted,
    addPatient,
    getAvailableDoctors,
    addReferredDoctor,
    removeReferredDoctor,
    mockAvailableDoctors,
  };

  return (
    <IpdDataContext.Provider value={value}>{children}</IpdDataContext.Provider>
  );
}

export default IpdDataContext;
